import { ScanGroupSchema, ScanLineItemSchema, ScanOptionSchema, ScanTaskSchema } from '@kaiizen/kai-scan-template-system';
import {
  apiDate,
  boolean,
  optional,
  optionalApiDate,
  optionalBoolean,
  optionalNumber,
  optionalString,
  optionalUuid,
  string,
  uuid,
  zodLess,
  ZodLessInfer,
  zunion,
} from '@kaiizen/zod-less';
import { ApiScanNoteSchema } from './ApiScanNoteSchema';

// *******************************************************************
// Scan Data Suggestion API
// Design: kai-wiki/work/plans/2026-10-15-scan-data-suggestions.md
// *******************************************************************

export const ScanDataSuggestionOperationSchema = zodLess('replace|add|remove');
export type ScanDataSuggestionOperation = ZodLessInfer<typeof ScanDataSuggestionOperationSchema>;

/** Resolved suggestions are kept, as history for improving the agent's suggestions. */
export const ScanDataSuggestionStatusSchema = zodLess('pending|accepted|rejected|superseded|obsolete');
export type ScanDataSuggestionStatus = ZodLessInfer<typeof ScanDataSuggestionStatusSchema>;

/**
 * How a client resolves a pending suggestion: accepted, rejected, or obsolete when applying finds its
 * target part (or an add's parent) gone. Superseding happens only on create, or when an alternative in the
 * same set is accepted.
 */
export const ScanDataSuggestionDecisionSchema = zodLess('accepted|rejected|obsolete');
export type ScanDataSuggestionDecision = ZodLessInfer<typeof ScanDataSuggestionDecisionSchema>;

/**
 * A serialized non-optimized ScanGroup, ScanTask, ScanOption or ScanLineItem subtree.
 *
 * Parsing strips unknown keys, so each member is strict at its top level: a task can't match the
 * line item or option schema and lose its `options`. Shallowest first, so whichever member matches
 * keeps every key the payload sent.
 */
export const ScanDataSuggestionScanPartSchema = zunion(
  ScanLineItemSchema.strict(),
  ScanOptionSchema.strict(),
  ScanTaskSchema.strict(),
  ScanGroupSchema.strict(),
);
export type ScanDataSuggestionScanPart = ZodLessInfer<typeof ScanDataSuggestionScanPartSchema>;

/**
 * The note's content. Its id, author, scan part and provenance are filled in when the suggestion is applied.
 * Only standalone note types: an edit or delete needs a parent note, and an approval would be recorded under
 * whoever applies the suggestion.
 */
export const ApiScanDataSuggestionNoteSchema = ApiScanNoteSchema.pick({
  body: true,
  visibility: true,
  regardingNamedValue: true,
  scanPhotoId: true,
}).extend({ scanNoteType: zodLess('info|instructions|editRequest') });
export type ApiScanDataSuggestionNote = ZodLessInfer<typeof ApiScanDataSuggestionNoteSchema>;

export const ApiScanDataSuggestionSchema = zodLess({
  id: uuid,
  createdAt: apiDate,
  scanId: uuid,
  operation: ScanDataSuggestionOperationSchema,
  scanPartId: string, // the target part; for add, the new part's id (scanPart's root id)
  parentScanPartId: optionalString, // required for add and remove
  basedOnUpdatedAt: optionalApiDate, // newest updatedAt in the target subtree; required for replace and remove
  scanPart: optional(ScanDataSuggestionScanPartSchema), // required for replace and add
  scanNote: optional(ApiScanDataSuggestionNoteSchema),
  createdByUserId: optionalUuid, // absent once the creating user is deleted

  status: ScanDataSuggestionStatusSchema,
  resolvedAt: optionalApiDate,
  resolvedByUserId: optionalUuid, // who accepted or rejected it, or whose newer replace superseded it

  // Generated by the creator and shared by suggestions offered together as alternatives for one part.
  // They don't supersede each other; accepting one supersedes the rest of its set.
  alternativeSuggestionSetId: optionalUuid,

  // provenance, as on ScanNote
  agentCreated: boolean,
  scanVideoId: optionalUuid,
  videoFrameTimeSeconds: optionalNumber,
});
export type ApiScanDataSuggestion = ZodLessInfer<typeof ApiScanDataSuggestionSchema>;

/** The creator is the signed-in user. A client-supplied id makes create idempotent. */
export const ApiCreateScanDataSuggestionSchema = ApiScanDataSuggestionSchema
  .omit({ id: true, createdAt: true, createdByUserId: true, agentCreated: true, status: true, resolvedAt: true, resolvedByUserId: true })
  .extend({ id: optionalUuid, agentCreated: optionalBoolean });
export type ApiCreateScanDataSuggestion = ZodLessInfer<typeof ApiCreateScanDataSuggestionSchema>;

/** The decision is the only update; who made it and when are filled in by the server. */
export const ApiUpdateScanDataSuggestionSchema = zodLess({ status: ScanDataSuggestionDecisionSchema });
export type ApiUpdateScanDataSuggestion = ZodLessInfer<typeof ApiUpdateScanDataSuggestionSchema>;

/**
 * A video's pending suggestions can only be superseded together: a re-run of the video does it
 * first, so only the new run's suggestions are pending.
 */
export const ApiUpdateScanDataSuggestionsByScanVideoSchema = zodLess({ status: zodLess('superseded') });
export type ApiUpdateScanDataSuggestionsByScanVideo = ZodLessInfer<typeof ApiUpdateScanDataSuggestionsByScanVideoSchema>;

export const getScanDataSuggestionQueryKey = (scanDataSuggestionId: string | undefined) => `scanDataSuggestion/${scanDataSuggestionId ?? ''}`;
export const getScanDataSuggestionsQueryKey = (scanId: string | undefined) => `scanDataSuggestions/byScanId/${scanId ?? ''}`;
